import {
  BankOutlined,
  CalendarOutlined,
  CloseOutlined,
  DeleteOutlined,
  DollarOutlined,
  EditOutlined,
  HeartFilled,
  PhoneOutlined,
  PlusOutlined,
  ProjectOutlined,
  SaveOutlined,
  ScheduleOutlined,
  ShoppingOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Descriptions,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Timeline,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useState } from "react";
import { ProjectStatus } from "../../enum/common";
import { DepartmentCode } from "../../enum/roles";
import { IProjectMember, IResearchProject } from "../../models/Research";
import { useGetEmployeeByDepartment } from "../../query/HrmQuery";
import {
  useCreateBulkProjectMember,
  useDeleteProjectMember,
  useGetProjectMember,
} from "../../query/ReseachQuery";
import { notify } from "../../utils/toast";
import ModalEditProject from "../modal/ModalEditProject";
import { IEmployeeByDepartment } from "../../models/Hrm";

const { Title, Text, Paragraph } = Typography;

interface ProjectInfoTabProps {
  project: IResearchProject | undefined;
}

const ProjectInfoTab = ({ project }: ProjectInfoTabProps) => {
  const [openModal, setOpenModal] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const { data: members = [], isLoading } = useGetProjectMember(project?.id!);
  const createBulkMemberMutation = useCreateBulkProjectMember();
  const deleteMemberMutation = useDeleteProjectMember();
  const { data: usersOfDepartment, isLoading: userDepartmentLoading } =
    useGetEmployeeByDepartment(DepartmentCode.RND);

  const handleDelete = async (id: number) => {
    try {
      await deleteMemberMutation.mutateAsync({
        id,
        projectId: project?.id!,
      });
      notify.success("Xóa thành viên thành công");
    } catch (error) {
      notify.error("Xóa thành viên thất bại");
    }
  };

  const handleAddNew = async () => {
    if (!selectedEmployeeIds.length) {
      notify.error("Vui lòng chọn ít nhất một thành viên");
      return;
    }

    try {
      await createBulkMemberMutation.mutateAsync({
        data: selectedEmployeeIds,
        projectId: project?.id!,
      });
      notify.success("Thêm thành viên thành công");
      setSelectedEmployeeIds([]);
      setIsAdding(false);
    } catch (error) {
      notify.error("Thêm thành viên thất bại");
    }
  };

  const handleCancelAdd = () => {
    setIsAdding(false);
    setSelectedEmployeeIds([]);
  };

  const columns: ColumnsType<IProjectMember> = [
    {
      title: "Họ và tên",
      dataIndex: ["employee", "user", "fullName"],
      key: "fullName",
    },
    {
      title: "Vai trò",
      dataIndex: ["employee", "user", "position", "positionTitle"],
      key: "role",
    },
    {
      title: "Số điện thoại",
      dataIndex: ["employee", "phone"],
      key: "phone",
    },
    {
      title: "Thao tác",
      key: "action",
      width: 150,
      render: (_: any, record: IProjectMember) => {
        return (
          <Space>
            <Popconfirm
              title="Xóa thành viên"
              description="Bạn có chắc chắn muốn xóa thành viên này?"
              onConfirm={() => handleDelete(record.id)}
              okText="Xóa"
              cancelText="Hủy"
            >
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                size="small"
                loading={deleteMemberMutation.isPending}
              >
                Xóa
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const renderStatus = (status?: string) => {
    const statusConfig = {
      [ProjectStatus.NEW]: { color: "default", text: "Dự án mới tạo" },
      [ProjectStatus.DONE]: { color: "success", text: "Đã hoàn thành" },
      [ProjectStatus.IN_PROGRESS]: {
        color: "processing",
        text: "Đang thực hiện",
      },
      [ProjectStatus.CANCELLED]: { color: "error", text: "Đã hủy" },
    };
    const config =
      statusConfig[status as keyof typeof statusConfig] ||
      statusConfig[ProjectStatus.NEW];
    return (
      <Tag color={config.color} icon={<HeartFilled />}>
        {config.text}
      </Tag>
    );
  };

  return (
    <div>
      <Card
        style={{
          marginBottom: 16,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          border: "none",
        }}
      >
        <Row justify="space-between" align="top">
          <Col flex="auto">
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              <Space>
                <ProjectOutlined style={{ fontSize: 28, color: "white" }} />
                <Tag
                  color="rgba(255,255,255,0.3)"
                  style={{ color: "white", border: "none" }}
                >
                  {project?.project}
                </Tag>
              </Space>
              <Title level={2} style={{ color: "white", margin: "4px 0" }}>
                {project?.projectName}
              </Title>
              <Paragraph
                style={{
                  color: "rgba(255,255,255,0.9)",
                  fontSize: 16,
                  marginBottom: 8,
                }}
              >
                Mô tả: {project?.projectDescription || "Chưa có mô tả dự án"}
              </Paragraph>
              <Space size="large" wrap>
                <Space style={{ color: "white" }}>
                  <CalendarOutlined />
                  <Text style={{ color: "white" }}>
                    Yêu cầu: {project?.requestDate}
                  </Text>
                </Space>
                <Space style={{ color: "white" }}>
                  <ScheduleOutlined />
                  <Text style={{ color: "white" }}>
                    Deadline:{" "}
                    {project?.deadline
                      ? dayjs(project.deadline).format("DD/MM/YYYY")
                      : "-"}
                  </Text>
                </Space>
                <div>{renderStatus(project?.status)}</div>
              </Space>
            </Space>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="large"
              onClick={() => setOpenModal(true)}
              style={{
                background: "white",
                color: "#667eea",
                borderColor: "white",
              }}
            >
              Chỉnh sửa
            </Button>
          </Col>
        </Row>
      </Card>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Card bordered={false} style={{ borderLeft: "4px solid #1890ff" }}>
            <Statistic
              title="Tổng ngân sách"
              value={Number(project?.totalBudget || 0)}
              precision={0}
              valueStyle={{ color: "#1890ff", fontSize: 24 }}
              prefix={<DollarOutlined />}
              suffix="VND"
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card bordered={false} style={{ borderLeft: "4px solid #52c41a" }}>
            <Statistic
              title="Đã chi tiêu"
              value={project?.totalSpent ? Number(project.totalSpent) : 0}
              precision={0}
              valueStyle={{ color: "#52c41a", fontSize: 24 }}
              prefix={<DollarOutlined />}
              suffix={project?.totalSpent ? "VND" : ""}
            />
            {!project?.totalSpent && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Chưa có dữ liệu
              </Text>
            )}
          </Card>
        </Col>
      </Row>

      <Card
        title={
          <Space>
            <CalendarOutlined style={{ color: "#1890ff" }} />
            <span>Tiến độ dự án</span>
          </Space>
        }
        bordered={false}
        style={{ marginBottom: 16 }}
      >
        <Timeline
          items={[
            {
              color: "blue",
              dot: <CalendarOutlined style={{ fontSize: 16 }} />,
              children: (
                <div>
                  <Text strong>Ngày yêu cầu</Text>
                  <br />
                  <Text type="secondary">{project?.requestDate}</Text>
                </div>
              ),
            },
            {
              color: "cyan",
              dot: <CalendarOutlined style={{ fontSize: 16 }} />,
              children: (
                <div>
                  <Text strong>Ngày khởi chạy</Text>
                  <br />
                  <Text type="secondary">
                    {project?.startDate
                      ? dayjs(project.startDate).format("DD/MM/YYYY")
                      : "-"}
                  </Text>
                </div>
              ),
            },
            {
              color: "green",
              dot: <ScheduleOutlined style={{ fontSize: 16 }} />,
              children: (
                <div>
                  <Text strong>Ngày hoàn thành</Text>
                  <br />
                  <Text type="secondary">
                    {project?.deadline
                      ? dayjs(project.deadline).format("DD/MM/YYYY")
                      : "-"}
                  </Text>
                </div>
              ),
            },
          ]}
        />
      </Card>
      <Card
        title={
          <Space>
            <BankOutlined style={{ color: "#1890ff" }} />
            <span>Thông tin khách hàng & Liên hệ</span>
          </Space>
        }
        bordered={false}
        style={{ marginBottom: 16 }}
      >
        <Descriptions column={1} bordered>
          <Descriptions.Item
            label={
              <Space>
                <BankOutlined />
                Khách hàng
              </Space>
            }
          >
            <Text strong>{project?.customerName}</Text>
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <Space>
                <UserOutlined />
                Yêu cầu từ
              </Space>
            }
          >
            <Text strong>{project?.requestFrom}</Text>
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <Space>
                <PhoneOutlined />
                Liên hệ
              </Space>
            }
          >
            <Text strong>{project?.contactProject}</Text>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card
        title={
          <Space>
            <TeamOutlined style={{ color: "#1890ff" }} />
            <span>Đội ngũ phụ trách</span>
          </Space>
        }
        bordered={false}
        style={{ marginBottom: 16 }}
      >
        <Descriptions column={1} bordered>
          <Descriptions.Item
            label={
              <Space>
                <TeamOutlined />
                Sale Admin phụ trách
              </Space>
            }
          >
            <Text strong>{project?.saleAdInCharge}</Text>
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <Space>
                <TeamOutlined />
                Sale phụ trách
              </Space>
            }
          >
            <Text strong>{project?.saleInCharge || "-"}</Text>
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <Space>
                <ShoppingOutlined />
                Mua hàng phụ trách
              </Space>
            }
          >
            <Text strong>{project?.purchaseInCharge}</Text>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card
        title={
          <Space>
            <TeamOutlined style={{ color: "#1890ff" }} />
            <span>Thành viên dự án</span>
          </Space>
        }
        bordered={false}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsAdding(true)}
            disabled={isAdding}
          >
            Thêm thành viên
          </Button>
        }
      >
        {isAdding && (
          <Card
            size="small"
            style={{
              marginBottom: 16,
              borderColor: "#1890ff",
            }}
          >
            <div style={{ marginBottom: 16 }}>
              <Select
                mode="multiple"
                placeholder="Chọn thành viên tham gia dự án"
                value={selectedEmployeeIds}
                onChange={(values) => setSelectedEmployeeIds(values)}
                size="large"
                style={{ width: "100%" }}
                showSearch
                loading={userDepartmentLoading}
                options={usersOfDepartment?.map(
                  (employee: IEmployeeByDepartment) => ({
                    label: `${employee.user.fullName} - ${employee.user.position.positionTitle}`,
                    value: employee.id,
                    disabled: members.some(
                      (m: any) => m.employeeId === employee.id,
                    ),
                  }),
                )}
              />
            </div>
            <Space>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleAddNew}
                loading={createBulkMemberMutation.isPending}
              >
                Lưu
              </Button>
              <Button icon={<CloseOutlined />} onClick={handleCancelAdd}>
                Hủy
              </Button>
            </Space>
          </Card>
        )}

        <Table
          columns={columns}
          dataSource={members}
          rowKey="id"
          pagination={false}
          loading={isLoading}
          locale={{
            emptyText: "Chưa có thành viên nào",
          }}
        />
      </Card>

      <ModalEditProject
        open={openModal}
        project={project || null}
        onClose={() => setOpenModal(false)}
      />
    </div>
  );
};

export default ProjectInfoTab;
